import { format, utcToZonedTime } from "date-fns-tz";
import enGB from "date-fns/locale/en-GB";
import { camelCase, startCase } from "lodash";

import {
  FUNDING_SOURCE_TYPES,
  REPORT_HEADING_OVERRIDES,
  REPORT_ROW_OVERRIDES,
  REPORT_SUBHEADING_OVERRIDES,
  TRANSITION_SCENARIOS_OVERRIDES,
} from "./constants";
import {
  standardizeKey,
  standardizeObjectKeys,
} from "./utils/helpers/standardizer.helper";
import { REPORT_DATA_INDEXING } from "./utils/reports";

function isDateValidFormat(date) {
  // Assumes date format is "mm/dd/yyyy" or "m/dd/yyyy"
  if (/([0-1]?\d\/{1})([0-3]?\d\/{1})([12]{1}\d{3})/.test(date)) {
    const parts = date.split("/").map((p) => parseInt(p, 10));
    parts[0] -= 1;
    const d = new Date(parts[2], parts[0], parts[1]);
    return (
      d.getMonth() === parts[0] &&
      d.getDate() === parts[1] &&
      d.getFullYear() === parts[2]
    );
  }

  // Else, assumes date format is "yyyy-mm-dd"
  if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    const parts = date.split("-").map((p) => parseInt(p, 10));
    const d = new Date(parts[0], parts[1] - 1, parts[2]);

    return (
      d.getFullYear() === parts[0] &&
      d.getMonth() === parts[1] - 1 &&
      d.getDate() === parts[2]
    );
  }

  return false;
}

export function isValidDate(date) {
  if (date === null || date === "" || date === "-") return false;

  const formattedDate = new Date(date);
  return (
    date &&
    isDateValidFormat(date) &&
    formattedDate !== "Invalid Date" &&
    !isNaN(formattedDate)
  );
}

export function getConvertedDateTime(dateToBeConverted) {
  if (!dateToBeConverted) return null;

  try {
    const date = new Date(dateToBeConverted);
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const shortTimezone = format(utcToZonedTime(date, timezone), "zzzz", {
      timeZone: timezone,
      locale: enGB,
    })
      .split(" ")
      .map((word) => word.charAt(0))
      .join("");
    const convertedDateTime = format(
      utcToZonedTime(date, timezone),
      "MM/dd/yyyy | hh:mm a",
      {
        timeZone: timezone,
        locale: enGB,
      }
    );
    return `${convertedDateTime} ${shortTimezone}`;
  } catch (error) {
    console.error("ERROR: invalid date.");
  }

  return null;
}

export function getFormattedDate(dateToBeFormatted) {
  if (!dateToBeFormatted) return null;

  const date = new Date(dateToBeFormatted);
  const year = date.getFullYear();
  const month = (1 + date.getMonth()).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");

  return [year, month, day].join("-");
}

export function formatDate(dateString) {
  if (!dateString?.includes("/")) return dateString;

  const dateArray = dateString.split("/");
  return [dateArray[2], dateArray[0], dateArray[1]].join("-");
}

export const convertDateWithHyphen = (dateString) => {
  console.log(`YOO`);
  if (!dateString || !isValidDate(dateString)) return "";
  const [year, month, date] = dateString.split("-");
  return [month, date, year].join("-");
};

export const convertDate = (dateString) => {
  if (!dateString || !isValidDate(dateString)) return "";
  const [year, month, date] = dateString.split("-");
  return [month, date, year].join("/");
};

export const convertSidebarDate = (dateString) => {
  if (!dateString || !isValidDate(dateString)) return "";
  const date = new Date(dateString.replace(/-/g, "/"));
  const year = date.getFullYear();
  const day = date.getDate();
  const month = date.toLocaleString("default", {
    month: "short",
  });

  return `${month} ${day}, ${year}`;
};

export const convertCoverPageDate = (dateString, options = {}) => {
  if (!dateString || !isValidDate(dateString)) return "";
  const date = new Date(dateString.replace(/-/g, "/"));
  const year = date.getFullYear();
  const day = date.getDate();
  const month = date.toLocaleString("default", {
    month: options?.isMonthShort ? "short" : "long",
  });

  return `${month} ${day}, ${year}`;
};

export const sortAlphabeticalLabelSelectOptions = (a, b) => {
  if (a.label < b.label) return -1;
  else if (a.label > b.label) return 1;
  return 0;
};

export function isValidEmail(email) {
  // eslint-disable-next-line no-useless-escape
  return /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
    email
  );
}

export function isValidPassword(password) {
  // eslint-disable-next-line no-useless-escape
  return /(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[-+_!@#$%^&*.,?~])/.test(password);
}

export const getUrlSlug = (pathname) => pathname.split("/").filter(Boolean);

export const pathToClassNames = (paths) =>
  getUrlSlug(paths)
    .map((path) => `page--${path}`)
    .join(" ");

// Converts a string to kebab case.
export const kebabCase = (str) =>
  str &&
  str
    .match(/[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g)
    .map((x) => x.toLowerCase())
    .join("-");

export const pascalCase = (str) =>
  str && startCase(camelCase(str)).replace(/ /g, "");

// Converts a string/number to dollar format
export const toDollar = (price) =>
  Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
  }).format(price);

export const appendParamsToUrl = (url = "", params) => {
  const [searchUrl, searchParams] = url.split("?");
  const urlSearchParams = new URLSearchParams(searchParams || "");

  for (const [paramKey, paramValue] of Object.entries(params)) {
    if (paramValue) urlSearchParams.set(paramKey, paramValue);
  }
  return [searchUrl, urlSearchParams.toString()].join("?");
};

export const convertStringToNumber = (value) => {
  if (typeof value !== "string") return value;
  return Number(value.match(/\d+\.?\d*/g)?.join(""));
};

export const limitDecimal = (number, precision = 2) => {
  return Intl.NumberFormat("en-US", {
    maximumFractionDigits: precision,
    minimumFractionDigits: precision,
  }).format(number);
};

export const checkIfElementsExistInString = (str, arr) =>
  arr.some((v) => str.includes(v));

export const alphanumericAndAmp = (value) => {
  // accept alphanumeric and ampersand characters
  return value.replace(/[^A-Za-z0-9& ]/g, "");
};

export const alphanumericAmpAndComma = (value) => {
  // accept alphanumeric, ampersand and commas characters
  return value.replace(/[^A-Za-z0-9&, ]/g, "");
};

export const reportDataIndexingOverride = (
  data,
  assetType,
  reportType,
  sectionKey
) => {
  const standardizedPageName = standardizeKey(sectionKey);
  const standardizedReport = standardizeObjectKeys(
    REPORT_DATA_INDEXING[reportType]?.[assetType] || {}
  );
  const reportIndexingArray = standardizedReport[standardizedPageName] || [];
  if (reportIndexingArray?.length === 0) return data;

  return reportIndexingArray.map((name) => {
    return data.filter((obj) =>
      standardizeKey(obj.columntitle).includes(standardizeKey(name))
    )[0];
  });
};

export const getCqSiIncludeValue = (checkboxValue, maxValue) => {
  const isChecked = (value) => (value ? "Y" : "N");
  return checkboxValue === undefined && maxValue === 100
    ? "Y"
    : isChecked(checkboxValue);
};

export const getOwnerType = (toggleSelectedOption) => {
  return toggleSelectedOption === "left" ? "all" : "my";
};

export const getNamingOverrides = (
  value,
  assetType,
  reportType,
  pageName,
  type = "heading",
  subheadingValue = ""
) => {
  if (!value) return "";

  const overridesDict = {
    heading: REPORT_HEADING_OVERRIDES,
    subheading: REPORT_SUBHEADING_OVERRIDES,
    row: REPORT_ROW_OVERRIDES,
  };

  const overrides = overridesDict[type] || {};

  const noAsteriskValue = value.replaceAll("*", "");
  return (
    overrides[assetType]?.[reportType]?.[pageName]?.[noAsteriskValue] ||
    subheadingValue ||
    value
  );
};

export const getOverHeadTransitionLabelOverrides = (
  assetType,
  reportType,
  pageName
) => {
  return (
    TRANSITION_SCENARIOS_OVERRIDES[reportType]?.[assetType]?.[pageName] ||
    "Transition Scenarios[2]"
  );
};

export const isFundingSourceCashOnly = (fundingSource) =>
  standardizeKey(FUNDING_SOURCE_TYPES.cashOnly) ===
  standardizeKey(fundingSource);

export const isFundingSourceSecuritiesOnly = (fundingSource) =>
  standardizeKey(FUNDING_SOURCE_TYPES.securitiesOnly) ===
  standardizeKey(fundingSource);

export const isFundingSourceCashAndSecurities = (fundingSource) =>
  standardizeKey(FUNDING_SOURCE_TYPES.cashAndSecurities) ===
  standardizeKey(fundingSource);

export const getFundingSourceGivenUnstandardizedString = (
  unstandardizedFundingSource
) => {
  const key = Object.keys(FUNDING_SOURCE_TYPES).find(
    (key) => standardizeKey(key) === standardizeKey(unstandardizedFundingSource)
  );
  return FUNDING_SOURCE_TYPES[key];
};

export const transformTransitionScenarioLabel = (label) => {
  if (!label) return "";

  if (Array.isArray(label)) {
    return label.join(" ");
  }

  if (typeof label === "string") return label;

  return "Transition Scenario";
};
